/**
 * /compensation/benchmarks — Lenavio Comp.
 *
 * Three tabs:
 *   - Lookup:   type a job title → AI-estimate p25/p50/p75/p90 + caveats.
 *   - Org map:  scatter of every active employee's salary by title with
 *               benchmark range bands overlaid.
 *   - Equity:   pay-equity bar charts by gender / department / tenure.
 *
 * Plan-gated by ``compensation`` (Enterprise+/trialing). HR_ADMIN+
 * roles only.
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  BarChart3,
  Download,
  Loader2,
  Scale,
  Sparkles,
  TrendingUp,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";

import {
  fetchOrgBenchmark,
  fetchPayEquity,
  lookupBenchmark,
  type Benchmark,
  type OrgBenchmarkRow,
  type OrgBenchmarkSummary,
  type PayEquityReport,
} from "@/api/compensation";

import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { Tabs, TabPanel } from "@/components/ui/Tabs";

type TabId = "lookup" | "org" | "equity";

function fmtMoney(n: number | null | undefined, currency = "USD"): string {
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency} ${Math.round(n).toLocaleString()}`;
  }
}

function flagBadge(flag: OrgBenchmarkRow["flag"]) {
  switch (flag) {
    case "underpaid":
      return <Badge variant="danger">Underpaid</Badge>;
    case "overpaid":
      return <Badge variant="warning">Overpaid</Badge>;
    case "within":
      return <Badge variant="success">Within</Badge>;
    case "no_benchmark":
      return <Badge variant="default">No benchmark</Badge>;
    default:
      return <Badge variant="default">No salary</Badge>;
  }
}

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows
    .map((r) =>
      r
        .map((cell) => {
          const s = (cell ?? "").toString();
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(","),
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ════════════════════════════════════════════════════════════════════
// Lookup tab
// ════════════════════════════════════════════════════════════════════

function LookupTab() {
  const { addToast } = useToast();
  const [jobTitle, setJobTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [location, setLocation] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Benchmark | null>(null);

  async function onLookup() {
    if (!jobTitle.trim()) {
      addToast("error", "Enter a job title");
      return;
    }
    setLoading(true);
    try {
      const b = await lookupBenchmark({
        job_title: jobTitle.trim(),
        department: department.trim() || null,
        location: location.trim() || null,
        currency: currency.trim().toUpperCase() || "USD",
        refresh,
      });
      setResult(b);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Lookup failed";
      addToast("error", detail);
    } finally {
      setLoading(false);
    }
  }

  // Bar visualization scaled to p90 + 10%.
  const max = result ? result.market_p90 * 1.1 : 0;
  const pct = (v: number) => (max ? Math.max(2, (v / max) * 100) : 0);

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <Input
            label="Job title"
            placeholder="e.g. Senior Backend Engineer"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
          />
          <Input
            label="Department (optional)"
            placeholder="e.g. Engineering"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          />
          <Input
            label="Location"
            placeholder="e.g. San Francisco, CA"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
          <Input
            label="Currency"
            placeholder="USD"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          />
        </div>
        <div className="mt-4 flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm text-text-muted">
            <input
              type="checkbox"
              checked={refresh}
              onChange={(e) => setRefresh(e.target.checked)}
              className="rounded border-border"
            />
            Force refresh (bypass cache)
          </label>
          <Button onClick={onLookup} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Estimating…
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Estimate range
              </>
            )}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-lg font-semibold">{result.job_title}</div>
              <div className="text-sm text-text-muted">
                {[result.location, result.department].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
            <Badge
              variant={
                result.confidence === "high"
                  ? "success"
                  : result.confidence === "low"
                    ? "warning"
                    : "default"
              }
            >
              {result.confidence} confidence
            </Badge>
          </div>

          <div className="mt-6 space-y-3">
            {(["market_p25", "market_p50", "market_p75", "market_p90"] as const).map(
              (key, i) => {
                const labels = ["P25", "P50 (median)", "P75", "P90"] as const;
                const value = result[key];
                return (
                  <div key={key}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-muted">{labels[i]}</span>
                      <span className="font-medium tabular-nums">
                        {fmtMoney(value, result.currency)}
                      </span>
                    </div>
                    <div className="mt-1 h-3 w-full rounded-full bg-surface-muted">
                      <div
                        className="h-3 rounded-full bg-accent"
                        style={{ width: `${pct(value)}%` }}
                      />
                    </div>
                  </div>
                );
              },
            )}
          </div>

          {result.caveats && (
            <div className="mt-5 rounded-md border border-border bg-surface-muted p-3 text-sm text-text-muted">
              <div className="mb-1 flex items-center gap-2 font-medium text-text-primary">
                <AlertTriangle className="h-4 w-4" /> Caveats
              </div>
              {result.caveats}
            </div>
          )}
          <div className="mt-3 text-xs text-text-muted">
            Source: {result.source} · Updated{" "}
            {new Date(result.last_updated).toLocaleString()}
          </div>
        </Card>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Org map tab — scatter of employees vs market band
// ════════════════════════════════════════════════════════════════════

function OrgMapTab() {
  const { addToast } = useToast();
  const [data, setData] = useState<OrgBenchmarkSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchOrgBenchmark();
        if (!cancelled) setData(r);
      } catch (err: unknown) {
        const detail =
          (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail || "Failed to load";
        addToast("error", detail);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  // Group rows by title for the visual map.
  const groups = useMemo(() => {
    if (!data) return [] as { title: string; rows: OrgBenchmarkRow[] }[];
    const m = new Map<string, OrgBenchmarkRow[]>();
    for (const r of data.rows) {
      if (!m.has(r.title)) m.set(r.title, []);
      m.get(r.title)!.push(r);
    }
    return Array.from(m.entries())
      .map(([title, rows]) => ({ title, rows }))
      .sort((a, b) => b.rows.length - a.rows.length);
  }, [data]);

  // Choose a global scale: max of (max salary, p90) across all rows × 1.1.
  const maxScale = useMemo(() => {
    if (!data) return 1;
    let max = 0;
    for (const r of data.rows) {
      max = Math.max(max, r.current_salary || 0, r.market_p90 || 0);
    }
    return Math.max(1, max * 1.1);
  }, [data]);

  function onExport() {
    if (!data) return;
    const header = [
      "First name",
      "Last name",
      "Title",
      "Department",
      "Location",
      "Current salary",
      "Currency",
      "Market P25",
      "Market P50",
      "Market P75",
      "Market P90",
      "Flag",
    ];
    const rows = data.rows.map((r) => [
      r.first_name,
      r.last_name,
      r.title,
      r.department || "",
      r.location || "",
      r.current_salary != null ? String(Math.round(r.current_salary)) : "",
      r.currency,
      r.market_p25 != null ? String(Math.round(r.market_p25)) : "",
      r.market_p50 != null ? String(Math.round(r.market_p50)) : "",
      r.market_p75 != null ? String(Math.round(r.market_p75)) : "",
      r.market_p90 != null ? String(Math.round(r.market_p90)) : "",
      r.flag,
    ]);
    downloadCsv("compensation-benchmark.csv", [header, ...rows]);
  }

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center text-text-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }
  if (!data) return null;

  const pct = (v: number) => Math.max(0, Math.min(100, (v / maxScale) * 100));

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <Stat label="Employees" value={data.total_employees} />
          <Stat label="Benchmarked" value={data.benchmarked} />
          <Stat label="Underpaid" value={data.underpaid} tone="danger" />
          <Stat label="Overpaid" value={data.overpaid} tone="warning" />
          <Stat
            label="Missing"
            value={data.no_benchmark + data.no_salary}
            tone="muted"
          />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-text-muted">
            Run <strong>Lookup</strong> for each title to populate market bands.
            Salaries to the left of P25 are flagged underpaid; right of P90,
            overpaid.
          </p>
          <Button variant="ghost" onClick={onExport}>
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <div className="space-y-5">
          {groups.map(({ title, rows }) => {
            const sample = rows.find((r) => r.market_p25 != null) || null;
            return (
              <div key={title}>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="text-sm font-medium">
                    {title}{" "}
                    <span className="text-text-muted">({rows.length})</span>
                  </div>
                  {sample && (
                    <div className="text-xs text-text-muted tabular-nums">
                      P25 {fmtMoney(sample.market_p25, sample.currency)} · P50{" "}
                      {fmtMoney(sample.market_p50, sample.currency)} · P90{" "}
                      {fmtMoney(sample.market_p90, sample.currency)}
                    </div>
                  )}
                </div>
                <div className="relative h-12 w-full rounded-md border border-border bg-surface-muted">
                  {sample && sample.market_p25 != null && sample.market_p90 != null && (
                    <div
                      className="absolute inset-y-0 rounded-md bg-accent/15"
                      style={{
                        left: `${pct(sample.market_p25)}%`,
                        width: `${Math.max(2, pct(sample.market_p90) - pct(sample.market_p25))}%`,
                      }}
                      aria-hidden
                    />
                  )}
                  {sample?.market_p50 != null && (
                    <div
                      className="absolute inset-y-1 w-px bg-accent"
                      style={{ left: `${pct(sample.market_p50)}%` }}
                      aria-hidden
                    />
                  )}
                  {rows.map((r) =>
                    r.current_salary != null ? (
                      <div
                        key={r.employee_id}
                        title={`${r.first_name} ${r.last_name} · ${fmtMoney(r.current_salary, r.currency)}`}
                        className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 rounded-full ring-2 ring-bg-1 ${
                          r.flag === "underpaid"
                            ? "bg-danger"
                            : r.flag === "overpaid"
                              ? "bg-warning"
                              : r.flag === "within"
                                ? "bg-success"
                                : "bg-text-muted"
                        }`}
                        style={{ left: `${pct(r.current_salary)}%` }}
                      />
                    ) : null,
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-muted text-text-muted">
              <tr>
                <th className="px-4 py-2 text-left">Employee</th>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-right">Current</th>
                <th className="px-4 py-2 text-right">P50</th>
                <th className="px-4 py-2 text-right">P90</th>
                <th className="px-4 py-2 text-left">Flag</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.employee_id} className="border-t border-border">
                  <td className="px-4 py-2">
                    {r.first_name} {r.last_name}
                  </td>
                  <td className="px-4 py-2 text-text-muted">{r.title}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {fmtMoney(r.current_salary, r.currency)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-text-muted">
                    {fmtMoney(r.market_p50, r.currency)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-text-muted">
                    {fmtMoney(r.market_p90, r.currency)}
                  </td>
                  <td className="px-4 py-2">{flagBadge(r.flag)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: "danger" | "warning" | "muted";
}) {
  const cls =
    tone === "danger"
      ? "text-danger"
      : tone === "warning"
        ? "text-warning"
        : tone === "muted"
          ? "text-text-muted"
          : "text-text-primary";
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-text-muted">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${cls}`}>
        {value}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Equity tab
// ════════════════════════════════════════════════════════════════════

function EquityTab() {
  const { addToast } = useToast();
  const [data, setData] = useState<PayEquityReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchPayEquity(0.15);
        if (!cancelled) setData(r);
      } catch (err: unknown) {
        const detail =
          (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail || "Failed to load";
        addToast("error", detail);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  function onExport() {
    if (!data) return;
    const header = [
      "Group type",
      "Group",
      "Employees",
      "Avg salary",
      "Median salary",
      "Ratio vs overall",
      "Flagged",
    ];
    const out: string[][] = [header];
    const push = (groupType: string, items: PayEquityReport["by_gender"]) => {
      for (const g of items) {
        out.push([
          groupType,
          g.label,
          String(g.employee_count),
          String(Math.round(g.avg_salary)),
          String(Math.round(g.median_salary)),
          g.ratio_vs_overall.toFixed(3),
          g.flagged ? "yes" : "no",
        ]);
      }
    };
    push("Gender", data.by_gender);
    push("Department", data.by_department);
    push("Tenure", data.by_tenure);
    downloadCsv("pay-equity-report.csv", out);
  }

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center text-text-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }
  if (!data) return null;

  const groupMax = (groups: PayEquityReport["by_gender"]) =>
    Math.max(1, ...groups.map((g) => g.avg_salary)) * 1.1;

  const renderGroup = (
    title: string,
    icon: React.ReactNode,
    groups: PayEquityReport["by_gender"],
  ) => {
    const max = groupMax(groups);
    return (
      <Card className="p-5">
        <div className="mb-4 flex items-center gap-2 text-sm font-medium">
          {icon} {title}
        </div>
        {groups.length === 0 ? (
          <div className="text-sm text-text-muted">No data.</div>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => {
              const w = (g.avg_salary / max) * 100;
              return (
                <div key={g.key}>
                  <div className="flex items-center justify-between text-sm">
                    <span>
                      {g.label}{" "}
                      <span className="text-text-muted">({g.employee_count})</span>
                    </span>
                    <span className="tabular-nums">
                      {fmtMoney(g.avg_salary, data.currency)}{" "}
                      <span className="text-text-muted">
                        · {(g.ratio_vs_overall * 100).toFixed(0)}%
                      </span>
                      {g.flagged && (
                        <Badge variant="warning" className="ml-2">
                          flagged
                        </Badge>
                      )}
                    </span>
                  </div>
                  <div className="mt-1 h-3 w-full rounded-full bg-surface-muted">
                    <div
                      className={`h-3 rounded-full ${g.flagged ? "bg-warning" : "bg-accent"}`}
                      style={{ width: `${Math.max(2, w)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
            <Stat
              label="Org average"
              value={fmtMoney(data.overall_avg, data.currency)}
            />
            <Stat
              label="Org median"
              value={fmtMoney(data.overall_median, data.currency)}
            />
            <Stat
              label="Flag threshold"
              value={`±${Math.round(data.flag_threshold * 100)}%`}
            />
          </div>
          <Button variant="ghost" onClick={onExport}>
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
        <p className="mt-3 text-xs text-text-muted">
          Gender is inferred from the optional <code>pronouns</code> field on
          each employee record. Employees with no pronouns set are bucketed as
          "Undisclosed". Groups deviating from the org average by more than the
          flag threshold (and with ≥3 members) are flagged.
        </p>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {renderGroup("By gender", <Scale className="h-4 w-4" />, data.by_gender)}
        {renderGroup(
          "By department",
          <BarChart3 className="h-4 w-4" />,
          data.by_department,
        )}
        {renderGroup(
          "By tenure",
          <TrendingUp className="h-4 w-4" />,
          data.by_tenure,
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Page
// ════════════════════════════════════════════════════════════════════

export function CompensationBenchmarkPage() {
  const [tab, setTab] = useState<TabId>("lookup");

  return (
    <motion.div {...pageTransition} className="space-y-6">
      <PageHeader
        title="Compensation benchmarks"
        subtitle="AI-estimated market salary ranges, org-wide pay map, and pay equity."
      />

      <Tabs
        tabs={[
          { id: "lookup", label: "Lookup" },
          { id: "org", label: "Org map" },
          { id: "equity", label: "Equity" },
        ]}
        activeTab={tab}
        onChange={(id) => setTab(id as TabId)}
        aria-label="Compensation benchmark sections"
      />

      <TabPanel id="lookup" activeTab={tab}>
        <LookupTab />
      </TabPanel>
      <TabPanel id="org" activeTab={tab}>
        <OrgMapTab />
      </TabPanel>
      <TabPanel id="equity" activeTab={tab}>
        <EquityTab />
      </TabPanel>
    </motion.div>
  );
}

export default CompensationBenchmarkPage;
